import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import "./CustomSelect.css";

export const CustomSelect = forwardRef(({
  options = [],
  value,
  onChange,
  onInputChange,
  onMenuOpen,
  onMenuClose,
  filterOption,
  noOptionsMessage,
  placeholder = "",
  isDisabled = false,
  isLoading = false,
  autoFocus = false,
  styles = {},
  escapeClearsValue = false,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    }
  }));

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (filterOption) {
      const filtered = options.filter((option) => 
        filterOption(option, inputValue)
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [options, inputValue, filterOption]);

  const openMenu = () => {
    if (isDisabled) return;
    setIsOpen(true);
    onMenuOpen?.();
  };

  const closeMenu = () => {
    setIsOpen(false);
    setInputValue("");
    onMenuClose?.();
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (onInputChange) {
      onInputChange(newValue, { action: "input-change" });
    }
    
    if (!isOpen) {
      openMenu();
    }
  };

  const handleOptionClick = (option, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    onChange?.(option);
    closeMenu();

    setTimeout(() => {
      inputRef.current?.blur();
    }, 10);
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange?.({ value: null, label: null });
    setInputValue("");
    
    setTimeout(() => {
      inputRef.current?.focus();
    }, 10);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen && filteredOptions.length > 0) {
        handleOptionClick(filteredOptions[0], e);
      } else if (!isOpen && !isDisabled) {
        openMenu();
      }
      return;
    }
    
    if (escapeClearsValue && e.key === "Escape" && inputValue) {
      e.stopPropagation();
      setInputValue("");
      handleClear(e);
    }
  };

  const handleInputClick = () => {
    openMenu();
  };

  const handleContainerClick = () => {
    if (!isDisabled) {
      inputRef.current?.focus();
      openMenu();
    }
  };

  const getNoOptionsMessage = () => {
    if (noOptionsMessage) {
      return noOptionsMessage({ inputValue });
    }
    return "Нет опций";
  };

  const displayValue = isOpen ? inputValue : (value?.label || "");

  return (
    <div 
      className={`custom-select ${isDisabled ? "custom-select--disabled" : ""} ${isOpen ? "custom-select--menu-is-open" : ""}`}
      ref={containerRef}
      style={styles?.container}
    >
      <div 
        className="custom-select__control"
        onClick={handleContainerClick}
        style={styles?.control}
      >
        <input
          ref={inputRef}
          type="text"
          className="custom-select__input"
          value={displayValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onClick={handleInputClick}
          placeholder={placeholder}
          disabled={isDisabled}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          style={styles?.input}
        />
        
        <div className="custom-select__indicators">
          {value?.value && !isDisabled && !isLoading && (
            <button
              type="button"
              className="custom-select__clear-indicator"
              onMouseDown={handleClear}
              onTouchEnd={handleClear}
              aria-label="Очистить"
            >
              ×
            </button>
          )}
          {isLoading ? (
            <div className="custom-select__loading-indicator">
              <svg className="custom-select__spinner" viewBox="0 0 20 20">
                <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
          ) : (
            <div className="custom-select__dropdown-indicator">
              <svg height="20" width="20" viewBox="0 0 20 20">
                <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
              </svg>
            </div>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="custom-select__menu" style={styles?.menu}>
          <div className="custom-select__menu-list" style={styles?.menuList}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value || index}
                  className="custom-select__option"
                  onMouseDown={(e) => handleOptionClick(option, e)}
                  onTouchEnd={(e) => handleOptionClick(option, e)}
                  style={styles?.option}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="custom-select__no-options" style={styles?.noOptionsMessage}>
                {getNoOptionsMessage()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

